import React from 'react'
import { memo, observer } from '~/ui/component'
import { HBox, Label, Panel, Switch, VBox } from '~/ui/components'
import { FormField, FormFieldHeader } from '~/ui/form'
import { useFormField } from '~/ui/form/hooks'
import { layout } from '~/ui/styling'
import renderDynamicFieldAllowance from './allowances/index'
import { DynamicField, DynamicFormSchema } from './types'

export interface Props {
  schema: DynamicFormSchema
}

const DynamicFieldToolbar = memo('DynamicFieldToolbar', (props: Props) => {

  const {schema} = props

  const renderAsLabel = (field: DynamicField) => {
    if (field.type !== 'string') { return true }
    return !field.richText
  }

  //------
  // Rendering

  function render() {
    return (
      <HBox gap={layout.padding.s}>
        {schema.map(renderField)}
      </HBox>
    )
  }

  function renderField(field: DynamicField) {
    if (field.type === 'object') {
      return (
        <NestedFieldset
          key={field.name}
          name={field.name}
          caption={field.caption}
          label={field.label}
          required={field.required}
          schema={field.schema}
        />
      )
    } else {
      return (
        <FormField
          key={field.name}
          name={field.name}
          caption={false}
          label={field.label ?? false}
          required={field.required}
          instruction={field.instruction}
          renderAsLabel={renderAsLabel(field)}
          children={bind => renderDynamicFieldAllowance(field, {...bind, inputStyle: 'dark'} as any)}
        />
      )
    }
  }

  return render()

})

interface NestedFieldsetProps extends Props {
  name:      string
  caption?:  string | null
  label?:    string | {on: string, off: string} | null
  required?: boolean
}

const NestedFieldset = observer('DynamicFieldToolbar', (props: NestedFieldsetProps) => {

  const {name, caption, required, ...rest} = props
  const [value, setValue] = useFormField<object | null>(name)

  const enabled    = required || value != null
  const setEnabled = React.useCallback((enabled: boolean | null) => {
    if (enabled) {
      setValue({})
    } else {
      setValue(null)
    }
  }, [setValue])

  function render() {
    return (
      <VBox gap={layout.padding.inline.m}>
        {caption != null && (
          <FormFieldHeader
            caption={renderCaption()}
          />
        )}
        {enabled && (
          <Panel semi padding={layout.padding.s}>
            <DynamicFieldToolbar {...rest}/>
          </Panel>
        )}
      </VBox>
    )
  }

  function renderCaption() {
    if (required) {
      return caption
    } else {
      return (
        <HBox gap={layout.padding.inline.m}>
          <Switch
            isOn={enabled}
            onChange={setEnabled}
            small
          />
          <Label small caption dim flex='shrink'>
            {caption}
          </Label>
        </HBox>
      )
    }
  }

  return render()

})

export default DynamicFieldToolbar